"use client";

import Link from "next/link";
import { useCallback, useMemo } from "react";
import { useExploreRoute } from "../_lib/explore-route-context";
import type { ExploreCategory, ExploreCategorySlug } from "../types";
import { useScrollDirection } from "./use-scroll-direction";

interface StickyCategoryPillsProps {
  categories: ExploreCategory[];
  activeCategory?: ExploreCategorySlug;
  /** Active tag slugs to preserve in navigation links. */
  activeTags?: string[];
}

export function StickyCategoryPills({
  categories,
  activeCategory,
  activeTags = [],
}: StickyCategoryPillsProps) {
  const { exploreBase } = useExploreRoute();
  const isScrollingDown = useScrollDirection({
    threshold: 10,
    topThreshold: 100,
  });
  const scrollActiveIntoView = useCallback((node: HTMLAnchorElement | null) => {
    node?.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, []);

  const tagQuery = useMemo(() => {
    if (activeTags.length === 0) {
      return "";
    }
    return `?tags=${activeTags.join(",")}`;
  }, [activeTags]);

  const activePillClass =
    "border-[var(--explore-accent-yellow)] bg-[var(--explore-accent-yellow)] font-semibold text-[var(--explore-on-accent-text)]";
  const inactivePillClass =
    "border-[var(--explore-border)] bg-[var(--explore-bg-card)] text-[var(--explore-text-secondary)] hover:border-[var(--explore-text-secondary)]";

  return (
    <div
      className={`sticky z-40 mb-1 transition-transform duration-300 ease-out lg:static lg:mb-8 lg:translate-y-0 lg:transition-none ${
        isScrollingDown ? "-translate-y-[calc(100%+1px)]" : "translate-y-0"
      }`}
      style={{
        top: "var(--explore-header-h)",
        fontFamily: "var(--homepage-sans-stack)",
      }}
    >
      <div className="bg-[var(--explore-bg)]/95 py-2 backdrop-blur-[16px] lg:bg-transparent lg:backdrop-blur-none">
        <div className="flex gap-2 overflow-x-auto pr-10 pl-4 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] lg:mx-auto lg:max-w-[1320px] lg:flex-wrap lg:justify-center lg:overflow-x-visible lg:px-6 [&::-webkit-scrollbar]:hidden">
          <Link
            className={`flex min-h-[var(--explore-touch-target)] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 font-medium text-[0.85rem] no-underline transition-all lg:min-h-0 lg:text-[0.9rem] ${
              activeCategory === undefined ? activePillClass : inactivePillClass
            }`}
            href={`${exploreBase}${tagQuery}`}
            ref={
              activeCategory === undefined ? scrollActiveIntoView : undefined
            }
          >
            All
          </Link>
          {categories.map((cat) => {
            const isActive = activeCategory === cat.slug;
            const categoryPath = isActive
              ? exploreBase
              : `${exploreBase}/${cat.slug}`;
            return (
              <Link
                className={`flex min-h-[var(--explore-touch-target)] shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-4 py-2 font-medium text-[0.85rem] no-underline transition-all lg:min-h-0 lg:text-[0.9rem] ${
                  isActive ? activePillClass : inactivePillClass
                }`}
                href={`${categoryPath}${tagQuery}`}
                key={cat.slug}
                ref={isActive ? scrollActiveIntoView : undefined}
              >
                <span className="text-[0.9rem]">{cat.emoji}</span>
                {cat.displayName}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
